/*
 * df-email-suggest.js — Desplegable de dominios de correo.
 *
 * Al escribir la «@» en cualquier campo de email del tema aparece una lista
 * bajo el campo con los dominios más usados. Según se sigue escribiendo la
 * lista se filtra, y desaparece en cuanto no queda ninguno que encaje o el
 * dominio ya está escrito entero.
 *
 * Va por delegación en `document` en vez de enganchar cada input: así cubre
 * también los formularios que nacen después (df-capture, quiz, comentarios)
 * sin tener que observar el DOM.
 *
 * El panel se pinta en <body> con position:fixed y no dentro del formulario,
 * porque varios contenedores del tema recortan con overflow y se lo comerían.
 */
(function () {
  'use strict';

  /* Dominios ordenados por uso real en España. Solo se enseñan 6 a la vez. */
  var DOMAINS = [
    'gmail.com',
    'hotmail.com',
    'outlook.es',
    'hotmail.es',
    'yahoo.es',
    'icloud.com',
    'outlook.com',
    'yahoo.com',
    'live.com',
    'me.com',
    'msn.com',
    'telefonica.net',
    'proton.me',
    'protonmail.com',
    'terra.es',
    'gmx.es',
    'aol.com'
  ];
  var MAX_ITEMS = 6;
  var SELECTOR = 'input[type="email"], input[inputmode="email"], input[data-df-email-suggest]';

  var panel = null;
  var input = null;      // campo al que está pegado el panel ahora mismo
  var items = [];        // [{ el, domain }]
  var active = -1;       // opción resaltada; -1 = ninguna, para no robarle el Enter al formulario
  var localPart = '';
  var idSeed = 0;
  var raf = 0;
  var lastBox = '';

  /* ---------------------------------------------------------------- estilos */

  function injectStyles() {
    if (document.getElementById('df-eac-styles')) return;
    var css = [
      '.df-eac {',
      '  position: fixed; z-index: 2147483000; box-sizing: border-box;',
      '  margin: 6px 0 0; padding: 6px; list-style: none;',
      '  max-height: 244px; overflow-y: auto; -webkit-overflow-scrolling: touch;',
      '  background: #fff; color: var(--df-color-ink, #15110d);',
      '  border: 2px solid var(--df-color-ink, #15110d);',
      '  border-radius: var(--df-radius-sm, 10px);',
      '  box-shadow: var(--df-shadow-hard-ink-4, 4px 4px 0 #15110d);',
      '  font-family: var(--df-font-sans, inherit); font-size: 15px; line-height: 1.2;',
      '  opacity: 0; transform: translateY(-4px); transition: opacity .12s ease, transform .12s ease;',
      '}',
      '.df-eac.is-open { opacity: 1; transform: none; }',
      '.df-eac--above { margin: 0 0 6px; }',
      '.df-eac__item {',
      '  display: block; width: 100%; box-sizing: border-box;',
      '  padding: 10px 12px; border-radius: var(--df-radius-xs, 6px);',
      '  cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;',
      '  color: inherit;',
      '}',
      '.df-eac__item + .df-eac__item { margin-top: 2px; }',
      '.df-eac__item.is-active { background: var(--df-color-paper, #F0E8D5); }',
      '.df-eac__local { color: var(--df-color-ink-50, rgba(21,17,13,.5)); }',
      '.df-eac__typed { font-weight: 700; }',
      '@media (hover: hover) { .df-eac__item:hover { background: var(--df-color-paper, #F0E8D5); } }',
      '@media (prefers-reduced-motion: reduce) {',
      '  .df-eac { transition: none; transform: none; }',
      '}'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'df-eac-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------ utilidades */

  function isTarget(el) {
    return !!el &&
      el.nodeType === 1 &&
      el.matches(SELECTOR) &&
      !el.matches('[data-df-no-email-suggest]') &&
      !el.disabled &&
      !el.readOnly;
  }

  /*
   * Devuelve los dominios que siguen encajando con lo tecleado tras la «@».
   * Lista vacía = hay que cerrar: o el dominio ya está completo, o no existe
   * ninguno que empiece así.
   */
  function matchesFor(value) {
    var at = value.indexOf('@');
    if (at < 0) return null;                                  // aún no hay «@»
    if (value.indexOf('@', at + 1) !== -1) return null;       // dos «@»: no es un email
    var local = value.slice(0, at);
    var typed = value.slice(at + 1).toLowerCase();
    if (!local) return null;                                  // sin nombre no hay nada que completar
    if (/[\s,;]/.test(local)) return null;

    // Dominio ya escrito entero: no queda nada que sugerir.
    if (typed && DOMAINS.indexOf(typed) !== -1) return { local: local, typed: typed, list: [] };

    var out = [];
    for (var i = 0; i < DOMAINS.length && out.length < MAX_ITEMS; i++) {
      if (!typed || DOMAINS[i].indexOf(typed) === 0) out.push(DOMAINS[i]);
    }
    return { local: local, typed: typed, list: out };
  }

  /* ---------------------------------------------------------------- panel */

  function ensurePanel() {
    if (panel) return panel;
    injectStyles();
    panel = document.createElement('div');
    panel.className = 'df-eac';
    panel.id = 'df-eac-panel';
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Sugerencias de correo');
    panel.style.display = 'none';
    // Sin esto el clic robaría el foco al campo y el focusout cerraría el panel
    // antes de que llegue el click.
    panel.addEventListener('mousedown', function (e) { e.preventDefault(); });
    panel.addEventListener('click', function (e) {
      var item = e.target.closest('.df-eac__item');
      if (!item) return;
      accept(item.getAttribute('data-domain'));
    });
    document.body.appendChild(panel);
    return panel;
  }

  function render(local, typed, list) {
    var p = ensurePanel();
    p.textContent = '';
    items = [];
    active = -1;
    localPart = local;

    list.forEach(function (domain) {
      var el = document.createElement('div');
      el.className = 'df-eac__item';
      el.id = 'df-eac-opt-' + (idSeed++);
      el.setAttribute('role', 'option');
      el.setAttribute('aria-selected', 'false');
      el.setAttribute('data-domain', domain);

      var localSpan = document.createElement('span');
      localSpan.className = 'df-eac__local';
      localSpan.textContent = local + '@';
      el.appendChild(localSpan);

      if (typed) {
        var typedSpan = document.createElement('span');
        typedSpan.className = 'df-eac__typed';
        typedSpan.textContent = domain.slice(0, typed.length);
        el.appendChild(typedSpan);
        el.appendChild(document.createTextNode(domain.slice(typed.length)));
      } else {
        el.appendChild(document.createTextNode(domain));
      }

      p.appendChild(el);
      items.push({ el: el, domain: domain });
    });
  }

  /*
   * Borde inferior de lo que se ve de verdad. En el móvil el teclado no
   * encoge window.innerHeight —sigue diciendo 812 con medio móvil tapado—,
   * así que la lista se pintaría debajo del teclado. visualViewport sí lo
   * descuenta.
   */
  function viewBottom() {
    var vv = window.visualViewport;
    return vv ? vv.offsetTop + vv.height : window.innerHeight;
  }

  function place() {
    if (!panel || !input) return;
    var r = input.getBoundingClientRect();
    panel.style.width = r.width + 'px';
    panel.style.left = r.left + 'px';

    var h = panel.offsetHeight;
    var space = viewBottom() - r.bottom;
    if (space < h + 12 && r.top > h + 12) {
      panel.classList.add('df-eac--above');
      panel.style.top = (r.top - h - 6) + 'px';
    } else {
      panel.classList.remove('df-eac--above');
      panel.style.top = r.bottom + 'px';
    }
    lastBox = boxKey(r);
  }

  function boxKey(r) {
    return r.top + '|' + r.left + '|' + r.width + '|' + Math.round(viewBottom());
  }

  /*
   * El panel va en position:fixed, así que tiene que perseguir al campo
   * mientras la página se mueve. Con listeners de scroll/resize se quedaba
   * descolgado —el navegador los agrupa— y encima no cubren los movimientos
   * sin scroll (un modal que se abre, el teclado del móvil). Un rAF mientras
   * está abierto sale más barato de razonar y solo toca el DOM si el campo
   * se ha movido de verdad.
   */
  function follow() {
    if (!input) { raf = 0; return; }
    var r = input.getBoundingClientRect();
    // Campo desmontado o fuera de la vista: el desplegable no pinta nada ahí.
    if ((r.width === 0 && r.height === 0) ||
        r.bottom < 0 || r.top > window.innerHeight) {
      close();
      return;
    }
    if (boxKey(r) !== lastBox) place();
    raf = requestAnimationFrame(follow);
  }

  function open(target, local, typed, list) {
    input = target;
    render(local, typed, list);
    panel.style.display = 'block';
    place();
    // Un frame después para que la transición se vea al abrir.
    requestAnimationFrame(function () { if (panel) panel.classList.add('is-open'); });

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'true');
    input.setAttribute('aria-controls', 'df-eac-panel');
    input.removeAttribute('aria-activedescendant');

    if (!raf) raf = requestAnimationFrame(follow);
  }

  function close() {
    if (!panel || panel.style.display === 'none') return;
    panel.classList.remove('is-open');
    panel.style.display = 'none';
    panel.textContent = '';
    items = [];
    active = -1;
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      input = null;
    }
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    lastBox = '';
  }

  function isOpen() {
    return !!panel && panel.style.display === 'block' && items.length > 0;
  }

  function highlight(i) {
    items.forEach(function (it, n) {
      var on = n === i;
      it.el.classList.toggle('is-active', on);
      it.el.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    active = i;
    if (i >= 0 && input) {
      input.setAttribute('aria-activedescendant', items[i].el.id);
      var el = items[i].el;
      var top = el.offsetTop;
      var bottom = top + el.offsetHeight;
      if (top < panel.scrollTop) panel.scrollTop = top;
      else if (bottom > panel.scrollTop + panel.clientHeight) panel.scrollTop = bottom - panel.clientHeight;
    } else if (input) {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function accept(domain) {
    if (!input || !domain) return;
    var target = input;
    target.value = localPart + '@' + domain;
    close();
    target.focus();
    // Que se enteren las validaciones y los contadores de otros scripts.
    target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function refresh(target) {
    var m = matchesFor(target.value);
    if (!m || m.list.length === 0) { close(); return; }
    if (isOpen() && input === target) {
      render(m.local, m.typed, m.list);
      place();
    } else {
      open(target, m.local, m.typed, m.list);
    }
  }

  /* ---------------------------------------------------------------- eventos */

  document.addEventListener('input', function (e) {
    if (!isTarget(e.target)) return;
    refresh(e.target);
  });

  document.addEventListener('keydown', function (e) {
    if (!isTarget(e.target) || e.target !== input) return;

    // Los navegadores viejos mandan 'Esc', 'Down' y 'Up' en vez de los de ahora.
    var key = e.key;
    if (key === 'Esc') key = 'Escape';
    else if (key === 'Down') key = 'ArrowDown';
    else if (key === 'Up') key = 'ArrowUp';

    if (key === 'Escape') {
      if (isOpen()) {
        close();
        // Que el Escape no siga hasta el modal que contenga el formulario.
        e.stopPropagation();
        e.preventDefault();
      }
      return;
    }
    if (!isOpen()) return;

    if (key === 'ArrowDown') {
      e.preventDefault();
      highlight(active + 1 >= items.length ? 0 : active + 1);
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      highlight(active - 1 < 0 ? items.length - 1 : active - 1);
    } else if (key === 'Enter') {
      // Sin opción resaltada el Enter es del formulario, no nuestro.
      if (active >= 0) {
        e.preventDefault();
        accept(items[active].domain);
      } else {
        close();
      }
    } else if (key === 'Tab') {
      if (active >= 0) accept(items[active].domain);
      else close();
    }
  }, true);

  document.addEventListener('focusout', function (e) {
    if (e.target === input) close();
  });

  // Al tocar fuera (el clic dentro del panel ya no llega aquí: lo cancela el mousedown).
  document.addEventListener('mousedown', function (e) {
    if (!input) return;
    if (e.target !== input && !(panel && panel.contains(e.target))) close();
  });

  window.addEventListener('pagehide', close);
})();
